import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSwipeable } from 'react-swipeable';
import { Button } from '@/components/ui/button';
import { Menu, X } from 'lucide-react';
import { Brand } from '../Brand';
interface NavItem {
  href: string;
  label: string;
}
const navItems: NavItem[] = [{
  href: '#product',
  label: 'Product'
}, {
  href: '#how-it-works',
  label: 'How it Works'
}, {
  href: '#pricing',
  label: 'Pricing'
}, {
  href: '#faq',
  label: 'Resources'
}];
interface NavLinkProps {
  href: string;
  children: React.ReactNode;
  isActive?: boolean;
  onClick?: () => void;
}
const NavLink = ({
  href,
  children,
  onClick
}: NavLinkProps) => {
  return <motion.a href={href} className="relative text-sm text-foreground hover:text-primary block md:inline py-2" whileHover="hover" onClick={onClick} data-id="element-48">
      {children}
      <motion.span className="absolute bottom-0 left-0 h-[2px] bg-primary rounded-full" initial={{
      width: 0,
      x: '50%'
    }} variants={{
      hover: {
        width: '100%',
        x: 0,
        transition: {
          type: "spring",
          stiffness: 400,
          damping: 25
        }
      }
    }} data-id="element-49" />
    </motion.a>;
};
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 8);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Gesture controls for mobile menu
  const swipeHandlers = useSwipeable({
    onSwipedDown: () => setIsMenuOpen(false),
    trackMouse: false,
    delta: 50
  });
  // Header animation variants
  const headerVariants = {
    initial: {
      height: 80,
      backdropFilter: 'blur(8px)',
      backgroundColor: 'rgba(255, 255, 255, 0.8)' // white with 80% opacity
    },
    scrolled: {
      height: 64,
      backdropFilter: 'blur(16px)',
      backgroundColor: 'rgba(255, 255, 255, 0.8)' // Keep at 80% opacity
    }
  };
  return <>
      <motion.header className="fixed top-0 inset-x-0 z-[60] border-b header-container" initial="initial" animate={isScrolled ? "scrolled" : "initial"} variants={headerVariants} transition={{
      duration: 0.3,
      ease: "easeOut"
    }} style={{
      boxShadow: isScrolled ? '0 1px 3px rgba(0,0,0,0.05)' : 'none'
    }} data-id="element-50">
        <div className="container flex h-full items-center justify-between px-4" data-id="element-51">
          <Brand isScrolled={isScrolled} data-id="element-52" />
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center relative space-x-8" data-id="element-53">
            {navItems.map(item => <NavLink key={item.href} href={item.href} data-id="element-54">
                {item.label}
              </NavLink>)}
          </nav>
          {/* Desktop CTA and Mobile Menu Button */}
          <div className="flex items-center gap-4" data-id="element-55">
            {/* Enhanced CTA Button */}
            <div className="hidden md:block" data-id="element-56">
              <Button size="lg" className="btn-shimmer bg-primary hover:bg-sage-700 text-white" onClick={() => window.location.href = '/waitlist'} data-id="element-57">
                Get Early Access
              </Button>
            </div>
            
            {/* Mobile Menu Toggle - Always visible, changes icon based on state */}
            <motion.div className="md:hidden" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} data-id="element-58">
              <Button size="icon" variant="ghost" onClick={() => setIsMenuOpen(!isMenuOpen)} data-id="element-59">
                {isMenuOpen ? <X className="h-6 w-6" data-id="element-60" /> : <Menu className="h-6 w-6" data-id="element-61" />}
              </Button>
            </motion.div>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-62">
        {isMenuOpen && <>
            {/* Backdrop */}
            <motion.div className="fixed inset-0 bg-background/80 backdrop-blur-lg z-40 md:hidden" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsMenuOpen(false)} data-id="element-63" />
            
            {/* Menu Content */}
            <motion.nav {...swipeHandlers} className="fixed inset-0 z-50 md:hidden flex flex-col items-center justify-center" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-64">
              
              {/* Menu items with stagger */}
              <motion.div className="flex flex-col items-center space-y-8" initial="closed" animate="open" exit="closed" variants={{
            open: {
              transition: {
                staggerChildren: 0.05
              }
            },
            closed: {
              transition: {
                staggerChildren: 0.05,
                staggerDirection: -1
              }
            }
          }} data-id="element-65">
                {navItems.map(item => <motion.div key={item.href} variants={{
              open: {
                opacity: 1,
                y: 0
              },
              closed: {
                opacity: 0,
                y: 20
              }
            }} transition={{
              type: "spring",
              stiffness: 300,
              damping: 24
            }} data-id="element-66">
                    <NavLink href={item.href} onClick={() => setIsMenuOpen(false)} data-id="element-67">
                      <span className="text-2xl font-display text-slate-900 dark:text-linen-50 hover:text-primary" data-id="element-68">
                        {item.label}
                      </span>
                    </NavLink>
                  </motion.div>)}
                
                <motion.div variants={{
              open: {
                opacity: 1,
                y: 0
              },
              closed: {
                opacity: 0,
                y: 20
              }
            }} transition={{
              type: "spring",
              stiffness: 300,
              damping: 24
            }} data-id="element-69">
                  <Button size="lg" className="btn-shimmer bg-primary hover:bg-sage-700 text-white" onClick={() => {
                setIsMenuOpen(false);
                window.location.href = '/waitlist';
              }} data-id="element-70">
                    Get Early Access
                  </Button>
                </motion.div>
              </motion.div>
            </motion.nav>
          </>}
      </AnimatePresence>
    </>;
}